<template>
  <div class="h-screen bg-[#f7f7f7] flex flex-col overflow-hidden">
    <!-- Top nav -->
    <nav class="fixed top-0 left-0 right-0 z-40 flex items-center h-12 min-h-[3rem] bg-white border-b border-gray-200 px-4">
      <router-link to="/" class="flex items-center shrink-0">
        <img src="@/assets/tracktalent_logo.svg" alt="Logo" class="h-7" />
      </router-link>

      <div id="topbar-search" class="hidden md:flex absolute left-1/2 -translate-x-1/2 w-full max-w-2xl px-4"></div>

      <div class="flex items-center gap-2 ml-auto">
        <router-link to="/login">
          <Button variant="ghost" class="text-gray-600 hover:text-[#1a66e6] font-medium text-sm px-4 py-1.5 rounded-full transition-colors">
            Log In
          </Button>
        </router-link>
        <router-link to="/register">
          <Button class="text-white hover:bg-black rounded-full px-5 py-1.5 text-sm font-medium shadow-sm transition-all hover:shadow-md duration-200">
            Get Started
          </Button>
        </router-link>
      </div>
    </nav>

    <!-- Sidebar -->
    <aside
      class="fixed top-12 left-0 h-[calc(100vh-3rem)] bg-white border-r border-gray-200 flex flex-col overflow-hidden transition-all duration-300 ease-in-out z-40"
      :class="[
        isSidebarCollapsed ? 'w-16' : 'w-60',
        !isMobileSidebarOpen ? '-translate-x-full md:translate-x-0' : 'translate-x-0'
      ]"
    >
      <!-- Collapse toggle -->
      <div class="border-b border-gray-200 flex-shrink-0 flex items-center" :class="isSidebarCollapsed ? 'justify-center' : 'justify-end'">
        <button
          @click="isSidebarCollapsed = !isSidebarCollapsed"
          class="flex items-center px-3 py-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors duration-150"
          :title="isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        >
          <component :is="isSidebarCollapsed ? ChevronsRight : ChevronsLeft" class="w-4 h-4" />
        </button>
      </div>

      <!-- Nav links -->
      <div class="flex-1 min-h-0 overflow-y-auto">
        <div class="flex flex-col min-h-full">
          <div class="px-2 pb-2 pt-4">
            <span
              class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 px-2 block overflow-hidden transition-all duration-300"
              :class="isSidebarCollapsed ? 'opacity-0 h-0 mb-0' : 'opacity-100 h-4 mb-2'"
            >
              TrackTalent
            </span>
            <ul class="flex flex-col gap-0.5">
              <li v-for="link in navLinks" :key="link.name">
                <router-link
                  :to="link.path"
                  :title="isSidebarCollapsed ? link.name : undefined"
                  class="flex items-center gap-3 px-2 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100 transition-all duration-300"
                  :class="[
                    { 'bg-cosmicPurple/10 font-medium text-cosmicPurple': isActive(link.path) },
                    isSidebarCollapsed ? '!gap-0 pl-3.5' : ''
                  ]"
                  @click="isMobileSidebarOpen = false"
                >
                  <component
                    :is="link.icon"
                    class="w-5 h-5 shrink-0"
                    :class="isActive(link.path) ? 'text-cosmicPurple' : 'text-gray-400'"
                  />
                  <span
                    class="whitespace-nowrap overflow-hidden transition-all duration-300"
                    :class="isSidebarCollapsed ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[10rem]'"
                  >
                    {{ link.name }}
                  </span>
                </router-link>
              </li>
            </ul>
          </div>

          <DashboardFooter :collapsed="isSidebarCollapsed" class="mt-auto" />
        </div>
      </div>
    </aside>

    <!-- Main content -->
    <div
      class="pt-12 flex-1 min-h-0 flex flex-col overflow-y-auto transition-[padding-left] duration-300 ease-in-out"
      :class="isSidebarCollapsed ? 'md:pl-16' : 'md:pl-60'"
    >
      <div class="flex-1 min-h-0 p-4 py-2 w-full flex flex-col">
        <router-view class="flex-1 min-h-0" />
      </div>
    </div>

    <!-- Mobile sidebar toggle -->
    <button
      class="md:hidden fixed bottom-4 left-4 z-50 flex items-center justify-center w-10 h-10 bg-white border border-gray-200 rounded-full shadow-md"
      @click="isMobileSidebarOpen = !isMobileSidebarOpen"
      aria-label="Toggle Sidebar"
    >
      <Menu class="w-5 h-5 text-gray-600" />
    </button>

    <div
      v-if="isMobileSidebarOpen"
      class="md:hidden fixed inset-0 bg-black/30 z-30"
      @click="isMobileSidebarOpen = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { useLocalStorage } from '@vueuse/core'
import { Briefcase, Files, Plus, Menu, ChevronsLeft, ChevronsRight } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import DashboardFooter from '@/components/layout/DashboardFooter.vue'

const route = useRoute()

const isMobileSidebarOpen = ref(false)
const isSidebarCollapsed = useLocalStorage('sidebar-collapsed', false)

const navLinks = [
  { name: 'Job Opportunities', path: '/explore/jobs', icon: Briefcase },
  { name: 'My Applications', path: '/register', icon: Files },
  { name: 'New Application', path: '/register', icon: Plus },
]

function isActive(path: string) {
  if (path === '/explore/jobs') return route.path.startsWith('/explore/jobs')
  return false
}
</script>
